import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isoDateTime, paisa, type Money } from '@fleet/shared';
import {
  useBookingsQuery,
  useCreateDriverMutation,
  useDeleteDriverMutation,
  useDriverQuery,
  useUpdateDriverMutation,
} from '@/api/endpoints';
import { Button, LinkButton } from '@/design/Button';
import { Confirm } from '@/design/Confirm';
import { Identifier } from '@/design/Data';
import { ErrorState } from '@/design/EmptyState';
import {
  Checkbox,
  DateInput,
  Field,
  FieldRow,
  MoneyInput,
  PhoneInput,
  TextInput,
} from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import { checkLicence, checkName, checkPhone } from '@/lib/validate';
import { useTime } from '@/lib/time';

/** Adding or editing a driver. Four fields that matter and one that decides pay. */
export function DriverFormPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== undefined && id !== 'new';
  const navigate = useNavigate();

  const existing = useDriverQuery(id ?? '', { skip: !isEditing });
  const trips = useBookingsQuery({ driver: id ?? '' }, { skip: !isEditing });
  const { formatDateTime } = useTime();
  const [createDriver, creating] = useCreateDriverMutation();
  const [updateDriver, updating] = useUpdateDriverMutation();
  const [deleteDriver, removing] = useDeleteDriverMutation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const toast = useToast();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [licenceNo, setLicenceNo] = useState('');
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const [address, setAddress] = useState('');
  const [allowance, setAllowance] = useState<Money>(paisa(0));
  const [isWorking, setIsWorking] = useState(true);

  useEffect(() => {
    const driver = existing.data;
    if (driver === undefined) return;
    setName(driver.name);
    setPhone(driver.phone);
    setLicenceNo(driver.licence_no ?? '');
    setLicenceExpiry(driver.licence_expiry ?? '');
    setAddress(driver.address ?? '');
    setAllowance(paisa(driver.daily_allowance ?? 0));
    setIsWorking(driver.is_active !== false);
  }, [existing.data]);

  const saving = creating.isLoading || updating.isLoading;
  const problems = readApiError(creating.error ?? updating.error ?? removing.error);

  /**
   * Checked as it is typed, so the refusal arrives before the round trip.
   *
   * A field stays quiet until it has something in it: complaining at an empty
   * form nobody has touched is noise. Emptiness is handled by `readyToSave`,
   * which only turns the button on once the required ones are filled.
   */
  const nameProblem = name === '' ? undefined : checkName(name);
  const phoneProblem = phone === '' ? undefined : checkPhone(phone);
  const licenceProblem = checkLicence(licenceNo);
  const readyToSave =
    name !== '' &&
    phone !== '' &&
    nameProblem === undefined &&
    phoneProblem === undefined &&
    licenceProblem === undefined;

  async function save(): Promise<void> {
    const body = {
      name,
      phone,
      licence_no: licenceNo,
      licence_expiry: licenceExpiry === '' ? null : licenceExpiry,
      address,
      daily_allowance: allowance,
      is_active: isWorking,
    };

    // A refusal is not an exception here: the messages it carries are already
    // rendered under the fields they belong to. Without this the rejected
    // promise escaped as an unhandled error and the page stayed put with no
    // explanation, which is what a silent failure looks like from the counter.
    try {
      if (isEditing) {
        await updateDriver({ id, body }).unwrap();
      } else {
        await createDriver(body).unwrap();
      }
    } catch {
      return;
    }
    navigate('/drivers');
  }

  /**
   * Remove a driver who should never have been added.
   *
   * QA reported there was no way to delete one at all. The case this exists
   * for is a name typed wrong five minutes ago; the server refuses anyone who
   * has actually driven, because deleting them would take the driver's name
   * off bookings that have already been invoiced.
   */
  async function remove(): Promise<void> {
    if (!isEditing) return;
    try {
      await deleteDriver(id).unwrap();
      toast.done(`${name} removed.`);
      navigate('/drivers');
    } catch {
      // The server's sentence — usually "they have trips against them" — is
      // shown on the form. Closing the dialog is what puts it in view.
      setConfirmingDelete(false);
      toast.problem('Not deleted. The reason is on the form.');
    }
  }

  if (isEditing && existing.isError) {
    return (
      <ErrorState
        headline="This driver did not load"
        detail="Go back to the drivers list and open them again."
        onRetry={() => void existing.refetch()}
        retryLabel="Load again"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">{isEditing ? 'Edit driver' : 'Add driver'}</h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="flex flex-col gap-4">
          <Panel title="The driver" current>
            <PanelBody>
              <div className="flex flex-col gap-4">
                <FieldRow>
                  <Field label="Name" required error={nameProblem ?? problems.fields['name']}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        autoFocus
                        value={name}
                        onChange={setName}
                      />
                    )}
                  </Field>
                  <Field
                    label="Phone number"
                    required
                    error={phoneProblem ?? problems.fields['phone']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <PhoneInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={phone}
                        onChange={setPhone}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Licence number" error={licenceProblem ?? problems.fields['licence_no']}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        identifier
                        value={licenceNo}
                        onChange={setLicenceNo}
                      />
                    )}
                  </Field>
                  <Field
                    label="Licence expires"
                    error={problems.fields['licence_expiry']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <DateInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={licenceExpiry}
                        onChange={setLicenceExpiry}
                      />
                    )}
                  </Field>
                </FieldRow>

                {/* Where they live. A driver is trusted with a vehicle
                    worth more than a year of their pay, and this is what the
                    rental has if one does not come back. */}
                <Field label="Address" error={problems.fields['address']}>
                  {({ id: fieldId, describedBy }) => (
                    <textarea
                      id={fieldId}
                      aria-describedby={describedBy}
                      rows={2}
                      value={address}
                      onChange={(event) => setAddress(event.target.value)}
                      className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
                    />
                  )}
                </Field>

                <Field
                  label="Daily allowance"
                  error={problems.fields['daily_allowance']}
                >
                  {({ id: fieldId, describedBy }) => (
                    <MoneyInput
                      id={fieldId}
                      describedBy={describedBy}
                      value={allowance}
                      onChange={setAllowance}
                    />
                  )}
                </Field>

                <Checkbox
                  label="Still working here"
                  checked={isWorking}
                  onChange={setIsWorking}
                />
              </div>
            </PanelBody>
          </Panel>

          {/*
            What they have driven.

            Reported by QA: "history cannot be viewed of the driver who has
            left". Turning someone off does hide them from the counter's
            dropdown, which is the point — but it also meant their trips were
            unreachable from anywhere in the product, and a driver's record of
            work is exactly what gets asked for *after* they have gone.

            Only when editing: a driver who does not exist yet has no trips.
          */}
          {isEditing && (
            <Panel title="Trips driven" note="Most recent first">
              {trips.isLoading ? (
                <div className="h-24" aria-busy="true" />
              ) : (trips.data?.results.length ?? 0) === 0 ? (
                <PanelBody>
                  <p className="text-ink-muted">
                    No trips recorded against {name === '' ? 'this driver' : name} yet.
                  </p>
                </PanelBody>
              ) : (
                <ul className="divide-y divide-rule">
                  {(trips.data?.results ?? []).map((trip) => (
                    <li key={trip.id}>
                      <Link
                        to={`/bookings/${trip.id}`}
                        className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 hover:bg-paper"
                      >
                        <span className="flex flex-wrap items-baseline gap-2">
                          <Identifier>{trip.reference}</Identifier>
                          <span>{trip.customer_name}</span>
                        </span>
                        <span className="text-small text-ink-muted">
                          {formatDateTime(isoDateTime(trip.start_at))} ·{' '}
                          {trip.vehicle_name ?? 'No vehicle'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}

          {problems.detail !== undefined && (
            <p
              className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
              role="alert"
            >
              {problems.detail}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              intent="primary"
              size="touch"
              type="submit"
              disabled={saving || !readyToSave}
              title={readyToSave ? undefined : 'Something above still needs filling in.'}
            >
              {saving ? 'Saving' : isEditing ? 'Save changes' : 'Add driver'}
            </Button>
            <LinkButton to="/drivers" size="touch">
              Cancel
            </LinkButton>

            {/* Pushed to the far end, away from Save, and only worth offering
                when there is nothing to lose: a driver with trips behind them
                is refused by the server anyway. */}
            {isEditing && (trips.data?.results.length ?? 0) === 0 && (
              <Button
                intent="danger"
                size="touch"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving || removing.isLoading}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </form>

      <Confirm
        open={confirmingDelete}
        title={`Delete ${name || 'this driver'}?`}
        detail={
          <>
            <p>They disappear from the drivers list entirely. This cannot be undone.</p>
            <p className="mt-2 text-ink-muted">
              A driver with trips behind them will be refused — turn off{' '}
              <span className="font-medium">Still working here</span> instead, which takes them
              out of the counter&rsquo;s list and keeps their history.
            </p>
          </>
        }
        confirmLabel="Delete driver"
        tone="alert"
        busy={removing.isLoading}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}
